/**
 * Public Website Privacy Policy for ergshoot.com.
 *
 * Required for Stripe (payment processor) onboarding/compliance — must be
 * reachable without authentication. Linked from the Landing page footer and
 * served at /privacy. Adapted to UK GDPR / Data Protection Act 2018 for
 * ERGSHOOT LTD (company no. 16359370).
 */

import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = '31 May 2026';
const VERSION = '1.0';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-10 w-auto" />
          </Link>
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            ← Back to home
          </Link>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold">Website Privacy Policy</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Version {VERSION} · Effective {EFFECTIVE_DATE}
          </p>
        </div>

        <div className="space-y-8 text-[15px] leading-relaxed text-muted-foreground">
          {/* 1. Purpose */}
          <section>
            <h2 className="mb-3 text-xl font-semibold text-foreground">1. Purpose</h2>
            <p>
              ERGSHOOT LTD (“the Company”, “we”, “us” or “our”) is committed to protecting your
              personal information when you use our website, products and services. We recognise
              that when you choose to provide us with information about yourself, you trust us to
              treat it in a responsible manner.
            </p>
            <p className="mt-3">
              The purpose of this Website Privacy Policy is to inform you about how the Company may
              use your personal information. It explains:
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>what information we may collect about you;</li>
              <li>how we will use information we collect about you;</li>
              <li>whether the Company will disclose your details to anyone else;</li>
              <li>where we might send your information;</li>
              <li>the use of cookies on the Company’s website; and</li>
              <li>how you can reject cookies.</li>
            </ul>
            <p className="mt-3">
              The Company uses all personal information that you provide to us, or that we collect
              from you, in accordance with all applicable laws, including the UK General Data
              Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </section>

          {/* 2. Definitions */}
          <section>
            <h2 className="mb-3 text-xl font-semibold text-foreground">2. Definitions</h2>
            <p>In this Privacy Policy, the following definitions are used:</p>
            <dl className="mt-3 space-y-3">
              <div>
                <dt className="font-medium text-foreground">UK GDPR &amp; Data Protection Law</dt>
                <dd>
                  All legislation and regulations in force from time to time regulating the use of
                  personal data and the privacy of electronic communications, including the UK GDPR,
                  the Data Protection Act 2018, and any successor legislation.
                </dd>
              </div>
              <div>
                <dt className="font-medium text-foreground">ICO</dt>
                <dd>
                  The Information Commissioner’s Office — the supervisory authority for data
                  protection in the UK.
                </dd>
              </div>
              <div>
                <dt className="font-medium text-foreground">Personal Data</dt>
                <dd>
                  Any information relating to an identifiable person who can be directly or
                  indirectly identified from that information — for example, a person’s name, email
                  address, identification number, location or online identifier. The terms “personal
                  data” and “personal information” are used interchangeably within this policy.
                </dd>
              </div>
              <div>
                <dt className="font-medium text-foreground">Personal Data Breach</dt>
                <dd>
                  A breach of security leading to the accidental or unlawful destruction, loss,
                  alteration, unauthorised disclosure of, or access to, personal data transmitted,
                  stored or otherwise processed.
                </dd>
              </div>
              <div>
                <dt className="font-medium text-foreground">Special Categories of Personal Data</dt>
                <dd>
                  Data that needs more protection because it is sensitive — including data relating
                  to an individual’s health or fitness. Where you log workout results, body metrics
                  or other fitness data, this may constitute special category data, which we process
                  only with your consent and to provide the service you have requested.
                </dd>
              </div>
            </dl>
          </section>

          {/* 3. Scope */}
          <section>
            <h2 className="mb-3 text-xl font-semibold text-foreground">3. Scope</h2>
            <p>
              This Website Privacy Policy applies only to the actions of the Company and the use of
              the Company’s website and services at ergshoot.com.
            </p>
            <p className="mt-3">
              Our website may contain hyperlinks to websites owned and operated by third parties.
              These third-party websites have their own privacy policies and are also likely to use
              cookies. We recommend that you review those policies. We do not accept any liability
              for such third-party websites, and your use of them is at your own risk.
            </p>
          </section>

          {/* 4. Collection and Use of Data */}
          <section>
            <h2 className="mb-3 text-xl font-semibold text-foreground">
              4. The Collection and Use of Data
            </h2>

            <h3 className="mb-2 mt-4 font-medium text-foreground">
              4.1 How we collect information
            </h3>
            <p>
              This policy relates to the Company’s use of any personal information collected from
              you via:
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>the ErgShoot website and web application;</li>
              <li>account registration and sign-in;</li>
              <li>social media and messaging services; and</li>
              <li>any official email address or contact number.</li>
            </ul>

            <h3 className="mb-2 mt-4 font-medium text-foreground">
              4.2 What information we collect
            </h3>
            <p>
              When you sign up to, access or use the Company’s services, we may receive personal
              information about you. This may include:
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>your name;</li>
              <li>email address and telephone number;</li>
              <li>the gym or organisation you are affiliated with;</li>
              <li>
                fitness data you choose to record, such as workout results, scores, and optional
                body metrics (e.g. date of birth, gender, height, weight) used for leaderboards and
                progress tracking;
              </li>
              <li>
                billing and subscription details — payments are processed securely by our payment
                provider, Stripe. We do not store your full card details on our systems; and
              </li>
              <li>information collected about your use of ErgShoot’s services.</li>
            </ul>

            <h3 className="mb-2 mt-4 font-medium text-foreground">
              4.3 How we use the information
            </h3>
            <p>Depending on your use of our site, we will use your personal information to:</p>
            <ul className="mt-3 list-disc space-y-1 pl-6">
              <li>
                provide our services, activities and online content — including delivering
                programming, recording your scores and operating leaderboards;
              </li>
              <li>
                administer your account and subscription, including processing payments and
                commission to our agents and affiliates;
              </li>
              <li>contact you for reasons related to a service you have signed up for;</li>
              <li>respond to your requests and enquiries; and</li>
              <li>carry out analysis and research so that we may improve the services we offer.</li>
            </ul>
            <p className="mt-3">
              We process your personal information on the basis of performance of our contract with
              you, your consent (for example, for marketing or for processing fitness data), and our
              legitimate business interests where these are not overridden by your rights. You do
              not have to provide us with personal information, but if you choose not to, you may
              not be able to receive certain services or access certain parts of our website.
            </p>

            <h3 className="mb-2 mt-4 font-medium text-foreground">
              4.4 Where we send your information
            </h3>
            <p>
              Our infrastructure and certain service providers (including Stripe for payments and
              our hosting provider) may process or store data outside the UK. Where data is
              transferred to a country that has not been found to provide an adequate level of
              protection, we put in place appropriate safeguards — such as the UK International Data
              Transfer Agreement or Addendum — to ensure your personal information is protected.
            </p>
          </section>

          {/* 5. Cookies */}
          <section>
            <h2 className="mb-3 text-xl font-semibold text-foreground">5. Cookies</h2>
            <p>
              We may use information obtained from cookies or similar technologies. Cookies are
              small text files placed on your device to collect standard internet log information
              and visitor behaviour information. This is used to operate the service and to compile
              statistical reports on website activity.
            </p>
            <p className="mt-3">
              You can set your browser not to accept cookies, and{' '}
              <a
                href="https://www.aboutcookies.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                www.aboutcookies.org
              </a>{' '}
              explains how to remove cookies from your browser. If you do so, some features of our
              website may not function correctly.
            </p>
            <h3 className="mb-2 mt-4 font-medium text-foreground">5.1 What we use cookies for</h3>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>
                <span className="text-foreground">Strictly necessary cookies</span> — essential to
                enable you to move around our website and use its features, such as signing in and
                accessing secure areas. Without these, services you have asked for cannot be
                provided.
              </li>
              <li>
                <span className="text-foreground">Performance cookies</span> — collect anonymous,
                aggregated information about how visitors use our website so we can improve it.
              </li>
              <li>
                <span className="text-foreground">Functionality cookies</span> — remember choices
                you make to provide enhanced, more personal features.
              </li>
            </ul>
          </section>

          {/* 6. Keeping Data Secure */}
          <section>
            <h2 className="mb-3 text-xl font-semibold text-foreground">6. Keeping Data Secure</h2>
            <h3 className="mb-2 mt-4 font-medium text-foreground">6.1 Third-party websites</h3>
            <p>
              Our website links to third-party sites which we do not operate or endorse. These
              websites may use cookies and collect your personal information in accordance with
              their own privacy policies. This Privacy Policy does not apply to third-party
              websites, and we are not responsible for them.
            </p>
            <h3 className="mb-2 mt-4 font-medium text-foreground">
              6.2 How we protect your information
            </h3>
            <p>
              We take appropriate measures to ensure that any personal information you disclose to
              us is kept secure, accurate and up to date, and retained only for as long as is
              necessary for the purposes for which it is used. These measures include, but are not
              limited to, data encryption in transit and at rest.
            </p>
          </section>

          {/* 7. Your rights */}
          <section>
            <h2 className="mb-3 text-xl font-semibold text-foreground">7. What are your rights?</h2>
            <p>
              Under the UK GDPR, you have the following rights, which the Company will always work
              to uphold:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>
                <span className="text-foreground">Right to access</span> — to a copy of the personal
                information we hold about you and details of how we process it.
              </li>
              <li>
                <span className="text-foreground">Right to rectification</span> — to have any
                inaccuracies in your personal information corrected.
              </li>
              <li>
                <span className="text-foreground">Right to erasure</span> — to have your personal
                information erased, or our use of it restricted.
              </li>
              <li>
                <span className="text-foreground">Right to restrict use</span> — to “block” us from
                using your data or limit the way in which we use it.
              </li>
              <li>
                <span className="text-foreground">Right to data portability</span> — to receive your
                personal information in a structured, commonly-used, machine-readable format, or to
                have it sent to a third party where technically feasible.
              </li>
              <li>
                <span className="text-foreground">Right to object</span> — to object to our use of
                your data, including where we use it for our legitimate interests.
              </li>
            </ul>

            <h3 className="mb-2 mt-4 font-medium text-foreground">7.1 Right to opt out</h3>
            <p>
              You can request that we stop sending you marketing materials at any time. Electronic
              communications typically include an unsubscribe link. If for any reason that is not
              successful, please contact us using the details below.
            </p>

            <h3 className="mb-2 mt-4 font-medium text-foreground">7.2 How to contact us</h3>
            <p>
              If you have any questions about this policy, or wish to exercise any of your rights in
              relation to your personal information, please contact us:
            </p>
            <div className="mt-3 rounded-lg border border-border bg-card p-4 text-foreground">
              <p className="font-medium">ERGSHOOT LTD</p>
              <p className="text-sm text-muted-foreground">Company number 16359370</p>
              <p className="mt-2 text-sm">
                Registered office: Office One, Coldbath Square, Farringdon, London, England, EC1H
                5HL
              </p>
              <p className="mt-2 text-sm">
                Email:{' '}
                <a href="mailto:hello@ergshoot.com" className="text-primary hover:underline">
                  hello@ergshoot.com
                </a>
              </p>
              <p className="text-sm">
                Telephone:{' '}
                <a href="tel:+447453223007" className="text-primary hover:underline">
                  +44 07453 223 007
                </a>
              </p>
            </div>
            <p className="mt-3">
              You have the right to make a complaint to the supervisory authority if you are unhappy
              with how we have handled your personal information. In the UK, this is the Information
              Commissioner’s Office (
              <a
                href="https://ico.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                ico.org.uk
              </a>
              ).
            </p>
          </section>

          {/* 8. Governance */}
          <section>
            <h2 className="mb-3 text-xl font-semibold text-foreground">8. Policy Governance</h2>
            <p>
              Responsibility for this Website Privacy Policy rests with the management of ERGSHOOT
              LTD. We monitor for legal, regulatory and industry best-practice developments and
              review this policy at least every 12 months to ensure it remains fit for purpose.
            </p>
            <p className="mt-3">
              This policy has been approved by senior management and is effective from{' '}
              {EFFECTIVE_DATE}.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} ERGSHOOT LTD. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/" className="hover:text-primary transition-colors">
              Home
            </Link>
            <a href="mailto:hello@ergshoot.com" className="hover:text-primary transition-colors">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
